import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { ClinicalImpression } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Box, Button, Grid, Modal, Stack } from "@mantine/core";

import {
  ClientNote,
  ClientNoteStatus,
  updateClientNoteStatus,
} from "@/clients/zod-schemas/client-note";
import { TextWrapper } from "@/components/TextWrapper";
import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";
import { usDateTimeFormat } from "@/utils/datetime";

import { useClientNotes } from "../utils/hooks";

export interface ArchiveModalProps {
  openModal: boolean;
  modalHandlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  };
  refresh: boolean;
  setRefresh: (refresh: boolean | ((prev: boolean) => boolean)) => void;
}

export default function ArchiveClientReminderNote({
  openModal,
  modalHandlers,
  refresh,
  setRefresh,
}: ArchiveModalProps) {
  const [restoringNoteId, setRestoringNoteId] = useState<string | null>(null);
  const medplum = useMedplum();
  const { clientId } = useParams<{ clientId: string }>();
  const archivedReminderNotes = useClientNotes(
    clientId,
    medplum,
    ClientNoteStatus.Completed,
    refresh,
    ClientNote.Reminder,
  );

  const handleRestoreReminder = useCallback(
    async (note: ClinicalImpression) => {
      if (restoringNoteId) return;
      setRestoringNoteId(note.id || null);
      try {
        await updateClientNoteStatus({
          medplum,
          clinicalImpression: note,
          status: ClientNoteStatus.InProgress,
        });
        setRefresh((prev) => !prev);
      } finally {
        setRestoringNoteId(null);
      }
    },
    [medplum, setRefresh, restoringNoteId],
  );

  return (
    <Modal
      centered
      opened={openModal}
      onClose={modalHandlers.close}
      title={<TextWrapper size="xl">File Reminders</TextWrapper>}
    >
      <Box style={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden", paddingRight: 8 }}>
        <Stack>
          {archivedReminderNotes.map((note, index) => {
            const { assessorDisplay, authorDisplay, date, lastUpdated, summary } =
              getClinicalImpressionData(note);
            const archiveDate = usDateTimeFormat(lastUpdated);
            const creationDate = usDateTimeFormat(date);

            return (
              <Grid key={index}>
                <Grid.Col span={{ base: 12, sm: 9 }}>
                  <TextWrapper size="md" style={{ wordWrap: "break-word" }}>
                    {summary}
                  </TextWrapper>
                  <TextWrapper pt="xs" size="xs" c="gray.7" fs="italic">
                    Arc: {authorDisplay} {archiveDate}
                  </TextWrapper>
                  <TextWrapper size="xs" c="gray.5" fs="italic">
                    {assessorDisplay} {creationDate}
                  </TextWrapper>
                </Grid.Col>
                <Grid.Col span={{ base: 12, sm: 2 }}>
                  <Button
                    color="black"
                    variant="outline"
                    size="compact-xs"
                    disabled={restoringNoteId !== null}
                    loading={restoringNoteId === note.id}
                    onClick={() => handleRestoreReminder(note)}
                  >
                    Restore
                  </Button>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>
      </Box>
    </Modal>
  );
}
